import React, { useState } from 'react';
import Modal from './ui/Modal.jsx';

const STYLES = [
    {
        id: 'auto',
        label: 'Auto-Organize',
        sub: 'Auto-detects themes',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
        ),
    },
    {
        id: 'body_system',
        label: 'Body System',
        sub: 'Cardio · Neuro · GI…',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
    },
    {
        id: 'clinical_framework',
        label: 'Clinical Flow',
        sub: 'Path → Dx → Mgmt',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
        ),
    },
];

const FlashNotesActionModal = ({ folder, onClose, onGenerate, isGenerating }) => {
    const [style, setStyle] = useState('auto');
    const hasNotes = !!folder.flashNotes;

    return (
        <Modal title={`Flash Notes — "${folder.name}"`} onClose={onClose} size="md">

            {/* Organization style picker */}
            <div className="mb-5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Organization style</p>
                <div className="grid grid-cols-3 gap-2">
                    {STYLES.map(s => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setStyle(s.id)}
                            className={`rounded-xl p-3 text-center border transition-all ${
                                style === s.id
                                    ? 'border-teal-500/60 bg-teal-500/12 ring-1 ring-teal-500/30'
                                    : 'border-white/8 bg-white/3 hover:bg-white/6 hover:border-white/15'
                            }`}
                        >
                            <div className={`flex justify-center mb-1.5 ${style === s.id ? 'text-teal-300' : 'text-slate-500'}`}>
                                {s.icon}
                            </div>
                            <div className={`text-xs font-semibold leading-tight ${style === s.id ? 'text-teal-200' : 'text-slate-300'}`}>
                                {s.label}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{s.sub}</div>
                        </button>
                    ))}
                </div>
                {style === 'body_system' && (
                    <p className="mt-2 text-[11px] text-slate-500 px-1">Groups cards by anatomical system — best for physiology or broad clinical decks.</p>
                )}
                {style === 'clinical_framework' && (
                    <p className="mt-2 text-[11px] text-slate-500 px-1">Structures notes as Pathophysiology → Presentation → Diagnosis → Management → Complications.</p>
                )}
            </div>

            {/* Generating spinner */}
            {isGenerating && (
                <div className="mb-4 flex justify-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-800/80 ring-1 ring-white/10 px-3.5 py-1.5 text-xs text-slate-200">
                        <span className="h-3 w-3 rounded-full border-2 border-teal-400 border-t-transparent animate-spin" />
                        Generating Flash Notes…
                    </div>
                </div>
            )}

            {/* Existing notes preview */}
            {hasNotes && (
                <div className="mb-4 rounded-xl border border-white/10 bg-slate-900/70 p-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Current notes</div>
                            <div className="text-[12px] text-slate-400 line-clamp-2 leading-relaxed">
                                {(folder.flashNotes || '').replace(/[#*`>_-]/g, '').slice(0, 140)}
                                {(folder.flashNotes || '').length > 140 ? '…' : ''}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                            <button
                                onClick={() => onGenerate(folder, 'view', false, false, style)}
                                className="rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-teal-600 to-emerald-600 whitespace-nowrap"
                            >
                                View
                            </button>
                            <button
                                onClick={() => onGenerate(folder, 'export', false, false, style)}
                                className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-200 bg-slate-800/70 ring-1 ring-white/10 whitespace-nowrap"
                            >
                                Export
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Action buttons */}
            <div className="grid gap-2.5">
                {!hasNotes && (
                    <>
                        <button
                            onClick={() => onGenerate(folder, 'view', false, false, style)}
                            disabled={isGenerating}
                            className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 disabled:opacity-60 transition"
                        >
                            {isGenerating ? 'Generating…' : 'Generate & View'}
                        </button>
                        <button
                            onClick={() => onGenerate(folder, 'export', false, false, style)}
                            disabled={isGenerating}
                            className="w-full rounded-xl px-4 py-3 text-sm font-medium text-slate-200 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 disabled:opacity-60 transition"
                        >
                            {isGenerating ? 'Please wait…' : 'Generate & Export PDF'}
                        </button>
                    </>
                )}

                {hasNotes && (
                    <button
                        onClick={() => onGenerate(folder, 'view', true, false, style)}
                        disabled={isGenerating}
                        className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-rose-50 bg-rose-600/90 hover:bg-rose-600 disabled:opacity-60 transition"
                    >
                        {isGenerating ? 'Generating…' : 'Regenerate with Selected Style'}
                    </button>
                )}
            </div>

            <div className="mt-5 flex justify-end">
                <button
                    disabled={isGenerating}
                    onClick={onClose}
                    className="rounded-xl px-4 py-2 text-sm text-slate-300 bg-slate-800/70 hover:bg-slate-800 ring-1 ring-white/10 disabled:opacity-60 transition"
                >
                    Cancel
                </button>
            </div>
        </Modal>
    );
};

export default FlashNotesActionModal;
